<script setup>
import { computed, nextTick, ref } from 'vue'
import { X } from 'lucide-vue-next'
import UserSelect from '@User-manager/components/users/UserSelect.vue'
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { Alert, AlertDescription } from '../../../../../../../resources/js/components/ui/alert/index.js'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import { Input } from '../../../../../../../resources/js/components/ui/input/index.js'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { useConversationStore } from '../../stores/conversationStore.js'

const props = defineProps({
  currentUserId: {
    type: [Number, String, null],
    default: null,
  },
})

const emit = defineEmits(['conversationCreateStarted', 'conversationCreated', 'conversationCreateFailed'])

const { componentRef: dialog, openModal, closeModal } = useModalControl()
const conversationStore = useConversationStore()

const groupTitle = ref('')
const selectedUser = ref(null)
const selectedUsers = ref([])
const userSelect = ref(null)
const userSelectKey = ref(0)
const groupConversationError = ref('')

const selectedUserIds = computed(() => {
  const currentUserId = Number(props.currentUserId)

  return selectedUsers.value
    .map((user) => Number(user.id))
    .filter((userId) => Number.isInteger(userId) && userId > 0 && userId !== currentUserId)
})

const canCreateGroupConversation = computed(() => {
  return !conversationStore.isLoading && groupTitle.value.trim().length > 0 && selectedUserIds.value.length > 0
})

const groupTitleValidationMessage = computed(() => {
  return groupTitle.value.trim() ? '' : 'Enter a group title.'
})

const groupParticipantValidationMessage = computed(() => {
  return selectedUserIds.value.length > 0 ? '' : 'Select at least one other user.'
})

const shouldShowValidationMessages = computed(() => {
  return groupTitle.value.length > 0 || selectedUsers.value.length > 0
})

const groupValidationMessages = computed(() => {
  if (groupConversationError.value || !shouldShowValidationMessages.value) {
    return []
  }

  return [groupTitleValidationMessage.value, groupParticipantValidationMessage.value].filter(Boolean)
})

const handleUserSelected = (user) => {
  const userId = Number(user?.id)
  const currentUserId = Number(props.currentUserId)

  if (!Number.isInteger(userId) || userId <= 0) {
    resetUserSelect()
    return
  }

  if (userId === currentUserId) {
    groupConversationError.value = 'Select at least one other user.'
    resetUserSelect()
    return
  }

  if (selectedUsers.value.some((selected) => Number(selected.id) === userId)) {
    resetUserSelect()
    return
  }

  groupConversationError.value = ''
  selectedUsers.value = [...selectedUsers.value, user]
  resetUserSelect()
}

const removeSelectedUser = (userId) => {
  selectedUsers.value = selectedUsers.value.filter((user) => Number(user.id) !== Number(userId))
}

const handleOk = async (bvEvent) => {
  if (bvEvent) {
    bvEvent.preventDefault()
  }

  await submitGroupConversation()
}

const submitGroupConversation = async () => {
  if (!canCreateGroupConversation.value) {
    return
  }

  groupConversationError.value = ''
  emit('conversationCreateStarted')

  try {
    const conversation = await conversationStore.createConversation({
      type: 'group',
      title: groupTitle.value.trim(),
      participant_user_ids: selectedUserIds.value,
    })

    if (!conversation?.id) {
      return
    }

    resetFields()
    closeModal()
    emit('conversationCreated', conversation)
  } catch (error) {
    groupConversationError.value = errorMessageFrom(error, 'Unable to create group conversation.')
    emit('conversationCreateFailed', groupConversationError.value)
  }
}

const resetFields = () => {
  groupTitle.value = ''
  resetUserSelect({ focus: false })
  selectedUsers.value = []
  groupConversationError.value = ''
}

const resetUserSelect = async ({ focus = true } = {}) => {
  selectedUser.value = null
  userSelectKey.value += 1

  if (!focus) {
    return
  }

  await nextTick()
  userSelect.value?.focusInput()
}

const formatUser = (user) => {
  if (user?.name && user?.email) {
    return `${user.name} <${user.email}>`
  }

  return user?.name || user?.email || `User #${user?.id}`
}

const errorMessageFrom = (error, fallback) => {
  const validationErrors = error?.response?.data?.errors

  if (validationErrors) {
    const messages = Object.values(validationErrors).flat().filter(Boolean)

    if (messages.length > 0) {
      return messages.join(' ')
    }
  }

  return error?.response?.data?.message || error?.message || fallback
}

defineExpose({
  openModal,
  closeModal,
})
</script>

<template>
  <UmisisModal ref="dialog" title="Create Group" description="Name the group and select the people who should be included." ok-title="Create group" cancel-title="Cancel" :ok-disabled="!canCreateGroupConversation" @ok="handleOk" @show="resetFields">
    <div class="flex flex-col gap-4">
      <Alert v-if="groupConversationError" variant="destructive">
        <AlertDescription>{{ groupConversationError }}</AlertDescription>
      </Alert>

      <Field class="gap-1">
        <FieldLabel for="chat-group-title">
          Group title
        </FieldLabel>
        <Input id="chat-group-title" v-model="groupTitle" placeholder="Group title" :is-error="Boolean(groupTitleValidationMessage && groupTitle.length > 0)" />
        <FieldDescription v-if="groupTitleValidationMessage && groupTitle.length > 0" class="font-medium text-destructive">
          {{ groupTitleValidationMessage }}
        </FieldDescription>
      </Field>

      <div class="flex flex-col gap-2">
        <UserSelect :key="userSelectKey" ref="userSelect" v-model="selectedUser" label="Users" placeholder="Search users by name or email" :disabled="conversationStore.isLoading" @selected="handleUserSelected" />

        <div v-if="selectedUsers.length > 0" class="flex flex-col gap-2">
          <div v-for="user in selectedUsers" :key="user.id" class="flex items-center justify-between gap-3 rounded-md border bg-muted/20 px-3 py-2 text-sm">
            <span class="min-w-0 flex-1 truncate">{{ formatUser(user) }}</span>
            <Button type="button" variant="ghost" size="icon-sm" :disabled="conversationStore.isLoading" :aria-label="`Remove ${formatUser(user)}`" @click="removeSelectedUser(user.id)">
              <X class="h-4 w-4" />
            </Button>
          </div>
        </div>

        <FieldDescription v-if="groupParticipantValidationMessage && selectedUsers.length > 0" class="font-medium text-destructive">
          {{ groupParticipantValidationMessage }}
        </FieldDescription>
      </div>

      <div v-if="groupValidationMessages.length" class="flex flex-col gap-1 text-xs text-destructive">
        <p v-for="message in groupValidationMessages" :key="message">
          {{ message }}
        </p>
      </div>
    </div>
  </UmisisModal>
</template>
